import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import useFormSecurity from '../../../hooks/useFormSecurity';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiBriefcase, FiLock, FiMail, FiArrowRight, FiUserPlus, FiArrowLeft, FiAlertCircle } = FiIcons;

const UtrederLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { authenticateUser } = useData();
  const { validateSubmission, HoneypotField } = useFormSecurity('utreder_login');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateSubmission()) return;
    setError('');
    setLoading(true);

    try {
      // VIKTIG: authenticateUser er asynkron, må bruke await
      const user = await authenticateUser(email, password);
      
      if (user && (user.role === 'utreder' || user.role === 'superadmin')) {
        const success = await login(user, 'utreder');
        if (success) {
          navigate('/utreder/dashboard');
        } else {
          setError('Kunne ikke opprette sesjon.');
        }
      } else {
        setError('Ugyldig e-post eller passord for utreder-konto.');
      }
    } catch (err) {
      console.error(err);
      setError('Det oppstod en teknisk feil ved innlogging.');
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setEmail('utreder@hansen.no');
    setPassword('utreder123');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border-t-4 border-indigo-600"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiBriefcase} className="text-indigo-600 text-2xl" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Utrederportal</h1>
          <p className="text-gray-500 mt-2">Logg inn for å se dine saker og profil</p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 text-sm flex items-start gap-2 border border-red-100">
            <SafeIcon icon={FiAlertCircle} className="mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <HoneypotField />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">E-post</label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-3 top-3 text-gray-400" />
              <input 
                type="email" 
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="navn@selskap.no"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Passord</label>
            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute left-3 top-3 text-gray-400" />
              <input 
                type="password" 
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="••••••••"
              />
            </div>
          </div>

          <button 
            type="submit" 
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition-colors font-medium flex items-center justify-center gap-2 shadow-sm disabled:opacity-70"
          >
            {loading ? 'Logger inn...' : 'Logg inn'}
            <SafeIcon icon={FiArrowRight} />
          </button>
        </form>

        <div 
          onClick={fillDemo}
          className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200 text-sm cursor-pointer hover:bg-indigo-50 transition-colors group"
        >
          <div className="flex justify-between items-center mb-1">
            <p className="font-semibold text-gray-700">Demo Utreder:</p>
            <span className="text-xs text-indigo-600 opacity-0 group-hover:opacity-100">Klikk for å fylle ut</span>
          </div>
          <div className="text-gray-600 font-mono text-xs">
            utreder@hansen.no / utreder123
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-100 text-center space-y-4">
          <button 
            onClick={() => navigate('/utreder/register')}
            className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center justify-center gap-2"
          >
            <SafeIcon icon={FiUserPlus} />
            Registrer ny utreder
          </button>
          <button 
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-gray-600 text-sm font-medium flex items-center justify-center gap-1"
          >
            <SafeIcon icon={FiArrowLeft} />
            Tilbake til forsiden
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default UtrederLogin;